import { useRef, useState } from "react";
import { X, Upload, ImageOff, PartyPopper, Loader2, ArrowRight, Building2, Smartphone, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { sendOtp, resetRecaptcha } from "@/lib/firebase";
import { saveVerifiedPhone } from "@/lib/api";

const MAX_LOGO_SIZE_MB = 2;
const INPUT_CLS =
  "bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-11 px-3 w-full";
const LABEL_CLS = "overline block mb-1.5";

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

// A gentle, 2-step welcome flow shown right after first signup so people set
// their logo + company info immediately (instead of hunting for the Brand
// button later). Both steps are optional — users can skip.
export default function OnboardingDialog({ show, initialBrand, onSave, onSkip }) {
  const [step, setStep] = useState(1); // 1: logo, 2: company info, 3: phone
  const [draft, setDraft] = useState(initialBrand || {
    logo_data_url: "", company_name: "", tagline: "", address: "", contact: "", website: "",
  });
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);
  // Phone-verify sub-state
  const [phone, setPhone] = useState("+91");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [phoneStep, setPhoneStep] = useState("input"); // "input" → "otp" → "done"
  const [phoneError, setPhoneError] = useState("");

  if (!show) return null;

  const handleLogoUpload = async (files) => {
    if (!files?.length) return;
    const f = files[0];
    if (!/^image\//.test(f.type)) {
      toast.error("Please upload an image (PNG, JPG, WEBP, SVG)");
      return;
    }
    if (f.size > MAX_LOGO_SIZE_MB * 1024 * 1024) {
      toast.error(`Logo must be smaller than ${MAX_LOGO_SIZE_MB} MB`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(f);
      setDraft((d) => ({ ...d, logo_data_url: dataUrl }));
    } catch {
      toast.error("Could not read logo file");
    }
  };

  const finish = async () => {
    setBusy(true);
    try {
      await onSave(draft);
    } finally {
      setBusy(false);
    }
  };

  const skip = () => {
    onSkip?.();
  };

  return (
    <div
      data-testid="onboarding-dialog"
      className="fixed inset-0 z-50 bg-slate-900/25 backdrop-blur flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && skip()}
    >
      <div
        className="bg-white border border-slate-200 w-full max-w-lg rounded-xl shadow-2xl shadow-slate-900/10 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative border-b-4 border-[#2563EB] px-6 py-4">
          <button
            type="button"
            data-testid="onboarding-close"
            onClick={skip}
            className="absolute top-3 right-3 text-slate-400 hover:text-slate-900 transition-colors"
            aria-label="Skip"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2">
            <PartyPopper className="w-5 h-5 text-[#2563EB]" />
            <div className="overline text-[#2563EB]">Welcome — 60-second setup</div>
          </div>
          <h2 className="heading text-2xl font-bold mt-1">
            {step === 1 ? "Add your logo" : step === 2 ? "Add your company details" : "Add mobile for password recovery"}
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {step === 1
              ? "Uploaded once, applied to every PDF / Excel you export. Keeps quotes on your letterhead."
              : step === 2
              ? "These show up on the quotation cover page and in the export footer."
              : "Verify your mobile now so you can reset your password by SMS if you ever forget it."}
          </p>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mt-4">
            <StepDot active={step >= 1} label="Logo" testId="onboarding-step-1" />
            <StepDivider />
            <StepDot active={step >= 2} label="Company" testId="onboarding-step-2" />
            <StepDivider />
            <StepDot active={step >= 3} label="Mobile" testId="onboarding-step-3" />
          </div>
        </div>

        <div className="p-6">
          {step === 1 ? (
            <div data-step="1">
              <div
                data-testid="onboarding-logo-dropzone"
                onClick={() => fileInputRef.current?.click()}
                className="cursor-pointer border-2 border-dashed border-slate-300 hover:border-[#2563EB] bg-slate-50 hover:bg-blue-50/50 rounded-lg p-8 flex flex-col items-center gap-2 text-center transition-colors"
              >
                {draft.logo_data_url ? (
                  <>
                    <img
                      src={draft.logo_data_url}
                      alt="Your logo"
                      className="max-h-24 max-w-[220px] object-contain"
                    />
                    <div className="mono text-[11px] uppercase tracking-widest text-[#2563EB] mt-2">
                      Click to replace
                    </div>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-[#2563EB]" strokeWidth={1.5} />
                    <div className="heading text-lg font-semibold text-slate-900">Drop your logo</div>
                    <div className="text-xs text-slate-500">PNG / JPG / SVG · max 2 MB</div>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  data-testid="onboarding-logo-file-input"
                  onChange={(e) => handleLogoUpload(e.target.files)}
                />
              </div>
              {draft.logo_data_url && (
                <button
                  type="button"
                  data-testid="onboarding-logo-clear"
                  onClick={() => setDraft((d) => ({ ...d, logo_data_url: "" }))}
                  className="text-xs mono uppercase tracking-widest text-slate-500 hover:text-red-600 inline-flex items-center gap-1 mt-3"
                >
                  <ImageOff className="w-3.5 h-3.5" /> Remove logo
                </button>
              )}
            </div>
          ) : step === 2 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-slate-600 mono text-xs uppercase tracking-widest">
                <Building2 className="w-4 h-4" /> Details go on every export
              </div>
              <div>
                <label htmlFor="ob-company" className={LABEL_CLS}>Company name</label>
                <Input
                  id="ob-company"
                  data-testid="onboarding-company-input"
                  className={INPUT_CLS}
                  placeholder="Sabarmati Roadlines Pvt Ltd"
                  value={draft.company_name}
                  onChange={(e) => setDraft((d) => ({ ...d, company_name: e.target.value }))}
                  maxLength={120}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="ob-website" className={LABEL_CLS}>Website / GST</label>
                  <Input
                    id="ob-website"
                    data-testid="onboarding-website-input"
                    className={INPUT_CLS}
                    placeholder="sabarmati.co.in"
                    value={draft.website}
                    onChange={(e) => setDraft((d) => ({ ...d, website: e.target.value }))}
                  />
                </div>
                <div>
                  <label htmlFor="ob-contact" className={LABEL_CLS}>Phone</label>
                  <Input
                    id="ob-contact"
                    data-testid="onboarding-contact-input"
                    className={INPUT_CLS}
                    placeholder="+91-98765 43210"
                    value={draft.contact}
                    onChange={(e) => setDraft((d) => ({ ...d, contact: e.target.value }))}
                  />
                </div>
              </div>
              <div>
                <label htmlFor="ob-address" className={LABEL_CLS}>Address</label>
                <Input
                  id="ob-address"
                  data-testid="onboarding-address-input"
                  className={INPUT_CLS}
                  placeholder="Ahmedabad, Gujarat"
                  value={draft.address}
                  onChange={(e) => setDraft((d) => ({ ...d, address: e.target.value }))}
                />
              </div>
            </div>
          ) : (
            <PhoneVerifyStep
              phone={phone} setPhone={setPhone}
              otp={otp} setOtp={setOtp}
              confirmation={confirmation} setConfirmation={setConfirmation}
              phoneStep={phoneStep} setPhoneStep={setPhoneStep}
              busy={busy} setBusy={setBusy}
              error={phoneError} setError={setPhoneError}
              onVerified={() => toast.success("Mobile verified — you can now reset by SMS.")}
            />
          )}

          <div className="mt-6 flex items-center justify-between gap-2">
            <button
              type="button"
              data-testid="onboarding-skip"
              onClick={skip}
              className="mono text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors"
            >
              Skip for now
            </button>
            {step === 1 ? (
              <Button
                type="button"
                data-testid="onboarding-next"
                onClick={() => setStep(2)}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs h-10 px-4"
              >
                Next → Company
              </Button>
            ) : step === 2 ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  data-testid="onboarding-back"
                  onClick={() => setStep(1)}
                  className="mono text-xs uppercase tracking-widest text-slate-500 hover:text-slate-900 border border-slate-200 h-10 px-3 rounded-md"
                >
                  Back
                </button>
                <Button
                  type="button"
                  data-testid="onboarding-next-2"
                  disabled={busy}
                  onClick={async () => {
                    // Save brand + company info now, then move to phone.
                    setBusy(true);
                    try { await onSave(draft); } finally { setBusy(false); }
                    setStep(3);
                  }}
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs h-10 px-4"
                >
                  {busy ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">Next → Mobile <ArrowRight className="w-4 h-4" /></span>
                  )}
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                data-testid="onboarding-finish"
                onClick={() => onSkip?.()}
                className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs h-10 px-4"
              >
                {phoneStep === "done" ? "Finish →" : "Do this later"}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StepDot({ active, label, testId }) {
  return (
    <div
      data-testid={testId}
      className={
        "flex items-center gap-2 mono text-[11px] uppercase tracking-widest transition-colors " +
        (active ? "text-[#2563EB]" : "text-slate-400")
      }
    >
      <div
        className={
          "w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold " +
          (active ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-500 border border-slate-200")
        }
      >
        {label[0]}
      </div>
      {label}
    </div>
  );
}

function StepDivider() {
  return <div className="flex-1 h-px bg-slate-200" />;
}

// ---- Optional step 3: verify a mobile number via Firebase SMS OTP ----
function PhoneVerifyStep({
  phone, setPhone,
  otp, setOtp,
  confirmation, setConfirmation,
  phoneStep, setPhoneStep,
  busy, setBusy,
  error, setError,
  onVerified,
}) {
  const send = async (e) => {
    e.preventDefault();
    setError("");
    if (!phone.startsWith("+") || phone.length < 8) {
      setError("Enter your number with country code (e.g. +919876543210).");
      return;
    }
    setBusy(true);
    try {
      const conf = await sendOtp(phone.trim(), "onboarding-recaptcha");
      setConfirmation(conf);
      setPhoneStep("otp");
    } catch (e) {
      setError(e?.message || "Could not send the OTP. Double-check the number.");
      resetRecaptcha();
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    if (!otp || otp.length < 4) { setError("Enter the 6-digit code."); return; }
    setBusy(true);
    try {
      const cred = await confirmation.confirm(otp.trim());
      const idToken = await cred.user.getIdToken();
      await saveVerifiedPhone(phone.trim(), idToken);
      setPhoneStep("done");
      onVerified?.();
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "That code didn't match.");
    } finally {
      setBusy(false);
    }
  };

  const errBlock = error && (
    <div className="border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2 rounded-md">
      {error}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-slate-600 mono text-xs uppercase tracking-widest">
        <Smartphone className="w-4 h-4" /> Verify by SMS (optional)
      </div>
      {phoneStep === "input" && (
        <form onSubmit={send} className="space-y-3">
          <Input
            data-testid="onboarding-phone-input"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
            className="bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-sm h-11 px-3 w-full"
            required
          />
          {errBlock}
          <Button
            type="submit"
            data-testid="onboarding-phone-send"
            disabled={busy}
            className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs h-11 disabled:opacity-50"
          >
            {busy ? <span className="flex items-center gap-2 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Sending OTP…</span> : "Send OTP →"}
          </Button>
        </form>
      )}
      {phoneStep === "otp" && (
        <form onSubmit={verify} className="space-y-3">
          <p className="text-sm text-slate-500">
            Enter the 6-digit code sent to <span className="mono text-slate-900">{phone}</span>.
          </p>
          <Input
            data-testid="onboarding-phone-otp"
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ""))}
            className="bg-white border border-slate-200 rounded-md focus:ring-1 focus:ring-[#2563EB] focus:border-[#2563EB] mono text-2xl tracking-widest text-center h-14 px-3 w-full"
            placeholder="123 456"
            required
          />
          {errBlock}
          <Button
            type="submit"
            data-testid="onboarding-phone-verify"
            disabled={busy || otp.length < 4}
            className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs h-11 disabled:opacity-50"
          >
            {busy ? "Verifying…" : "Verify →"}
          </Button>
        </form>
      )}
      {phoneStep === "done" && (
        <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 rounded-md">
          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold">Mobile verified.</div>
            <div className="text-sm">You can now reset your password by SMS whenever needed.</div>
          </div>
        </div>
      )}
      <div id="onboarding-recaptcha" />
    </div>
  );
}
